import { IconButtonStyledOutlined } from '@/components/common/iconButtonStyledOutlined';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import AccountDetailsFlyout from '@/components/flyouts/AccountDetailsFlyout';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { AccountBase } from '@/models/AccountViewModel';
import SubcategoryGroupHeader from '@/components/table/SubcategoryGroupHeader';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { useGetAccountsQuery } from '@/store/accountsApi';
import { formatNumberToCurrency } from '@/utils/currency';
import { formatAppDate, parseOptionalDate } from '@/utils/date';
import { mobileWidth } from '@/constants';
import { PAGE_SIZE_OPTIONS, PAGE_SIZES } from '@/constants/pagination';
import {
  BALANCE_DESC_API_SORT,
  BALANCE_DESC_GRID_SORT_MODEL,
} from '@/constants/grid';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Link, Typography, useMediaQuery } from '@mui/material';
import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GridApiPremium,
  GridColumnVisibilityModel,
  GridColDef,
  GridListViewColDef,
  GridRenderCellParams,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import { skipToken } from '@reduxjs/toolkit/query';
import React, { useMemo, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import CustomToolbarForNative from '@/components/CustomToolbarForNative';
import XsPreferencesDrawerToolbar from '@/components/table/XsPreferencesDrawerToolbar';
import { ManagedColumnsManagement } from '@/components/table/ManagedColumnsManagement';
import { Capacitor } from '@capacitor/core';
import { actionColumnConfig } from './gridColumnConfigs';
import {
  GridContainer,
  GridContainerSize,
} from '@/components/grids/GridContainer';
import {
  BALANCE_SUM_AGGREGATION_MODEL,
  SUBCATEGORY_FIELD,
  hasSubcategories,
  isAccountRow,
  subcategoryOf,
  summarizeGroup,
} from '@/utils/subcategoryGrouping';

interface AccountsGridProps extends StandardWidgetProps {
  endDate: string;
  isWidget: boolean;
  filteredAccounts?: AccountBase[];
  /** Height of the grid in pixels. If not provided, grid will expand to fit content */
  gridHeight?: number;
  investorId?: number;
  onlyPrimaryInvestors?: boolean;
  /**
   * Groups accounts under their subcategory — the second tier of Account Type —
   * with a subtotal per group, the way legacy showed the balance sheet
   * drill-down. Off everywhere else, where this grid is a plain account list.
   */
  groupBySubcategory?: boolean;
  /** Size preset of the box around the grid. See `GridContainer`. */
  containerSize?: GridContainerSize;
}

type AccountsGridRow = AccountBase & {
  formattedSubtype: string;
};

/** Groups alphabetically, accounts within a group by balance as elsewhere. */
const GROUPED_SORT_MODEL = [
  { field: GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, sort: 'asc' as const },
  { field: 'Balance', sort: 'desc' as const },
];

/**
 * Trims a string field that the API may return as null/undefined at runtime even
 * though the model types it as a required string. The inner `?? ''` is the
 * runtime guard that keeps `.trim()` from crashing on optimistic types.
 */
const trimSafe = (value: string | null | undefined): string =>
  (value ?? '').trim();

/**
 * Coerces a string field the API may return as null/undefined at runtime into a
 * plain string without trimming. The `?? ''` is the runtime guard.
 */
const stringSafe = (value: string | null | undefined): string => value ?? '';

/**
 * Coerces a number field the API may return as null/undefined at runtime into a
 * number. The `?? 0` is the runtime guard against NaN/undefined.
 */
const numberSafe = (value: number | null | undefined): number => value ?? 0;

const AccountsGrid: React.FC<AccountsGridProps> = ({
  endDate,
  isWidget: _isWidget,
  filteredAccounts,
  gridHeight: _gridHeight,
  investorId,
  onlyPrimaryInvestors,
  groupBySubcategory = false,
  containerSize,
}) => {
  const [selectedAccount, setSelectedAccount] = useState<AccountBase | null>(
    null,
  );
  const authLoaded = useAuthIsLoaded();
  const apiRef = useGridApiRef();
  const { t } = useLocalization();
  const isXs = useMediaQuery(mobileWidth);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  const isNative = Capacitor.getPlatform() !== 'web';

  const {
    data: accountsData,
    isLoading,
    isError,
  } = useGetAccountsQuery(
    !filteredAccounts && authLoaded
      ? {
          showClosed: true,
          asOfDate: endDate,
          ...(typeof investorId === 'number' ? { InvestorId: investorId } : {}),
          ...(typeof onlyPrimaryInvestors !== 'undefined'
            ? { OnlyPrimaryInvestors: onlyPrimaryInvestors }
            : {}),
          sort: BALANCE_DESC_API_SORT,
        }
      : skipToken,
  );

  const accountRecords: AccountBase[] = useMemo(
    () => accountsData?.Records ?? [],
    [accountsData],
  );

  const rows: AccountsGridRow[] = useMemo(() => {
    const accounts: AccountBase[] = filteredAccounts ?? accountRecords;

    return accounts.map((account, index) => ({
      id: account.AccountId || index,
      ...account,
      formattedSubtype: (
        account.SubClassificationName ||
        account.ClassificationName ||
        ''
      )
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
    }));
  }, [accountRecords, filteredAccounts]);

  const total = useMemo(() => {
    const accounts: AccountBase[] = filteredAccounts ?? accountRecords;
    return accounts.reduce((acc, item) => acc + item.Balance, 0);
  }, [accountRecords, filteredAccounts]);

  const uncategorizedLabel = t('uncategorized', 'Uncategorized');

  const isGrouped = useMemo(
    () => groupBySubcategory && hasSubcategories(rows),
    [groupBySubcategory, rows],
  );

  // The default sum renders a "sum" chip beside the figure; these rows are
  // already labelled by the group they sit under.
  const aggregationFunctions = useMemo(
    () => ({
      ...GRID_AGGREGATION_FUNCTIONS,
      sum: { ...GRID_AGGREGATION_FUNCTIONS.sum, label: '' },
    }),
    [],
  );

  const handleAccountSelect = (account: AccountBase) => {
    setSelectedAccount(account);
  };

  const handleCloseModal = () => {
    setSelectedAccount(null);
  };

  const listViewColDef: GridListViewColDef<AccountsGridRow> = useMemo(
    () => ({
      field: 'listColumn',
      renderCell: (params) => {
        const { rowNode } = params;

        // Subcategory header. The list column is the whole row on mobile, so the
        // group row has to draw its own name, toggle and subtotal.
        if (rowNode.type === 'group') {
          const key = rowNode.groupingKey;
          const groupName =
            key == null || String(key).trim() === ''
              ? uncategorizedLabel
              : String(key);
          const isExpanded = !!rowNode.childrenExpanded;
          const gridApi = params.api as GridApiPremium;
          const { count, total: subtotal } = summarizeGroup(gridApi, params.id);

          return (
            <SubcategoryGroupHeader
              name={groupName}
              count={count}
              total={subtotal}
              isExpanded={isExpanded}
              onToggle={() => {
                gridApi.setRowChildrenExpansion(params.id, !isExpanded);
              }}
            />
          );
        }

        const visibilityModel = params.api.state.columns.columnVisibilityModel;
        // A field absent from the model is `undefined` at runtime (⇒ visible); the
        // cast keeps that `!== false` guard since the index type is optimistically
        // `boolean`.
        const isFieldVisible = (field: string) =>
          (visibilityModel[field] as boolean | undefined) !== false;

        const showName = isFieldVisible('Name');
        const showBalance = isFieldVisible('Balance');
        const showAccountNumber = isFieldVisible('AccountNumber');
        const showAccountType = isFieldVisible('formattedSubtype');
        const showLastUpdated = isFieldVisible('LastUpdated');

        const accountName = showName ? stringSafe(params.row.Name) : t('name');
        const balance = showBalance
          ? formatNumberToCurrency(numberSafe(params.row.Balance))
          : '';

        const accountNumber = stringSafe(params.row.AccountNumber);
        const accountType = stringSafe(params.row.formattedSubtype);
        const lastUpdated = formatAppDate(params.row.LastUpdated, {
          format: 'M/D/YYYY',
          fallback: '',
        });

        return (
          <Box
            sx={{
              width: '100%',
              py: 2,
              px: { xs: 0, sm: 0 },
              cursor: 'pointer',
            }}
            onClick={() => {
              handleAccountSelect(params.row);
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
              }}
            >
              <Typography
                variant='body2'
                color='text.primary'
                sx={{
                  fontWeight: '600 !important',
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  color: showName ? 'text.primary' : 'transparent',
                }}
              >
                {accountName}
              </Typography>

              <Typography
                variant='body2'
                color='text.primary'
                sx={{
                  fontWeight: '600 !important',
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                }}
              >
                {balance}
              </Typography>
            </Box>

            {showAccountNumber && accountNumber ? (
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ fontWeight: 400, display: 'block', mt: 0.5 }}
              >
                {t('grid.acctNumber', 'Acct #:')} {accountNumber}
              </Typography>
            ) : null}

            {showAccountType && accountType ? (
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ fontWeight: 400, display: 'block' }}
              >
                {accountType}
              </Typography>
            ) : null}

            {showLastUpdated && lastUpdated ? (
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ fontWeight: 400, display: 'block' }}
              >
                {t('grid.updated', 'Updated')} {lastUpdated}
              </Typography>
            ) : null}
          </Box>
        );
      },
    }),
    [t, uncategorizedLabel],
  );

  const columns: GridColDef<AccountsGridRow>[] = [
    {
      field: 'Name',
      headerName: t('name'),
      flex: 1,
      // EUD-440: clicking the account name opens the Account Details flyout.
      renderCell: (params: GridRenderCellParams<AccountsGridRow>) => {
        const name = stringSafe(params.row.Name);
        if (!name) return null;
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minHeight: '100%',
              py: 1,
              cursor: 'pointer',
            }}
            onClick={() => {
              handleAccountSelect(params.row);
            }}
          >
            <Typography
              variant='body2'
              sx={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {name}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'AccountNumber',
      headerName: t('grid.accountNumber'),
      flex: 1,
      renderCell: (params: GridRenderCellParams<AccountsGridRow>) => {
        const accountNumber = trimSafe(params.row.AccountNumber);
        const accountUrl = trimSafe(params.row.AccountUrl);

        if (!accountNumber) {
          return '';
        }

        if (isXs || !accountUrl) {
          return accountNumber;
        }

        return (
          <Link
            href={accountUrl}
            target='_blank'
            rel='noopener noreferrer'
            underline='hover'
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {accountNumber}
          </Link>
        );
      },
    },
    {
      field: 'Balance',
      headerName: t('grid.balance'),
      type: 'number',
      valueFormatter: (value: number | null | undefined) => {
        if (!value) {
          return value;
        }
        return formatNumberToCurrency(value);
      },
      flex: 1,
    },
    {
      field: 'formattedSubtype',
      headerName: t('grid.accountType'),
      flex: 1,
    },
    {
      field: 'LastUpdated',
      headerName: t('grid.lastUpdated'),
      type: 'date',
      valueGetter: (value: string) => parseOptionalDate(value),
      renderCell: (params: GridRenderCellParams<AccountsGridRow, Date>) =>
        formatAppDate(params.value, { format: 'M/D/YYYY', fallback: '' }),
      flex: 1,
    },
    {
      field: SUBCATEGORY_FIELD,
      headerName: t('grid.accountType'),
      valueGetter: (_value, row: AccountsGridRow) =>
        subcategoryOf(row) ?? uncategorizedLabel,
    },
    {
      field: 'actions',
      width: 60,
      ...actionColumnConfig,
      renderCell: (params: GridRenderCellParams<AccountsGridRow>) => {
        // Group headers carry no account, so the details action has nothing to
        // open.
        if (!isAccountRow(params.rowNode)) {
          return null;
        }
        return (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconButtonStyledOutlined
              onClick={() => {
                handleAccountSelect(params.row);
              }}
            >
              <MoreVertIcon />
            </IconButtonStyledOutlined>
          </Box>
        );
      },
    },
  ];

  if (isLoading || (!filteredAccounts && !authLoaded)) {
    return <LoadingSpinner />;
  }

  if (isError && !filteredAccounts) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Typography color='error'>Error loading accounts data.</Typography>
      </Box>
    );
  }

  // The grouping column is internal, and while grouped the per-row Account Type
  // column renders the same value the group header already shows.
  const effectiveColumnVisibilityModel: GridColumnVisibilityModel = {
    ...columnVisibilityModel,
    [SUBCATEGORY_FIELD]: false,
    ...(isGrouped ? { formattedSubtype: false } : {}),
  };

  const hasLoadedRecords = accountsData?.Records != null;
  if ((!hasLoadedRecords && !filteredAccounts) || rows.length === 0) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Typography>No accounts data available.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <GridContainer
        isWidget={_isWidget}
        size={containerSize}
      >
        {/* EUD-886: the drill-down still lists every account when the category has
          no subcategories to group by, but it says so rather than looking like
          grouping silently failed. */}
        {groupBySubcategory && !isGrouped ? (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mb: 1, px: 1 }}
          >
            {t(
              'grid.groupingUnavailable',
              'Account type grouping is unavailable for these accounts.',
            )}
          </Typography>
        ) : null}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DataGridPremium
            rows={rows}
            columns={columns}
            apiRef={apiRef}
            disableRowSelectionOnClick
            showToolbar
            disablePivoting
            pagination
            pageSizeOptions={[...PAGE_SIZE_OPTIONS.STANDARD]}
            listView={isXs}
            listViewColumn={listViewColDef}
            // Rows size to their content on every breakpoint so a long account
            // name wraps instead of being clipped mid-word.
            getRowHeight={() => 'auto'}
            columnVisibilityModel={effectiveColumnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            rowGroupingModel={isGrouped ? [SUBCATEGORY_FIELD] : []}
            groupingColDef={{ headerName: t('grid.accountType'), flex: 1.5 }}
            aggregationModel={isGrouped ? BALANCE_SUM_AGGREGATION_MODEL : {}}
            aggregationFunctions={aggregationFunctions}
            // The grand total already sits below the grid, so only the groups are
            // aggregated; a root footer row would repeat it.
            getAggregationPosition={(groupNode) =>
              groupNode.depth === -1 ? null : 'inline'
            }
            initialState={{
              pagination: {
                paginationModel: { pageSize: PAGE_SIZES.DEFAULT },
              },
              sorting: {
                sortModel: isGrouped
                  ? GROUPED_SORT_MODEL
                  : BALANCE_DESC_GRID_SORT_MODEL,
              },
            }}
            slotProps={{
              toolbar: {
                ...defaultToolbarExportSlotProps,
                showQuickFilter: true,
              },
              // Keep list/internal columns out of the Columns drawer; allow `actions` to be toggled.
              columnsManagement: {
                getTogglableColumns: (cols: GridColDef[]) =>
                  cols
                    .filter((c) => c.hideable !== false)
                    .map((c) => c.field || '')
                    .filter(
                      (field) =>
                        field !== 'actions' &&
                        field !== 'listColumn' &&
                        field !== '__list__' &&
                        field !== SUBCATEGORY_FIELD &&
                        !(isGrouped && field === 'formattedSubtype') &&
                        !field.startsWith('__'),
                    ),
              },
            }}
            slots={{
              columnsManagement: ManagedColumnsManagement,
              ...(isNative
                ? {
                    toolbar: (
                      props: React.ComponentProps<
                        typeof CustomToolbarForNative
                      >,
                    ) => <CustomToolbarForNative {...props} />,
                  }
                : isXs
                  ? { toolbar: XsPreferencesDrawerToolbar }
                  : {}),
            }}
            sx={
              isXs
                ? {
                    '& .MuiDataGrid-cell': {
                      width: '100%',
                      minWidth: '100%',
                    },
                  }
                : {}
            }
          />
        </Box>
      </GridContainer>

      <Box
        sx={{
          mt: 2,
          mb: 1,
          px: 1,
          fontWeight: 600,
          display: 'flex',
          justifyContent: 'space-between',
          borderTop: 1,
          borderColor: 'divider',
          pt: 1.5,
        }}
      >
        <Typography variant='subtitle1'>Total</Typography>
        <Typography variant='subtitle1'>
          {formatNumberToCurrency(total)}
        </Typography>
      </Box>

      {selectedAccount && (
        <AccountDetailsFlyout
          open={!!selectedAccount}
          toggleDrawer={handleCloseModal}
          account={selectedAccount}
          investorId={investorId}
        />
      )}
    </Box>
  );
};

export default AccountsGrid;
